import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  PermissionCreateFormData,
  permissionCreateSchema,
  PermissionUpdateFormData,
  permissionUpdateSchema,
} from "../../schemas/permissionSchema";

interface PermissionFormProps {
  initialData?: Partial<PermissionUpdateFormData>;
  onSubmit: (data: PermissionCreateFormData | PermissionUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const PermissionForm: React.FC<PermissionFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const form = useForm<PermissionCreateFormData | PermissionUpdateFormData>({
    resolver: zodResolver(
      isUpdate ? permissionUpdateSchema : permissionCreateSchema
    ),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData as any,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = form;

  const handleFormSubmit = async (
    data: PermissionCreateFormData | PermissionUpdateFormData
  ) => {
    await onSubmit(data);
  };

  const commonActions = [
    "create",
    "read",
    "update",
    "delete",
    "view",
    "manage",
    "approve",
    "reject",
  ];

  const commonModules = [
    "tenant",
    "user",
    "role",
    "permission",
    "screen",
    "creative",
    "playlist",
    "schedule",
  ];

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit as any)}
      className="space-y-4"
    >
      <div className="space-y-4">
        <Input
          label="Action"
          placeholder="e.g., tenant.create, user.update, role.delete"
          name="action"
          value={watch("action") || ""}
          onChange={(e) => setValue("action", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("action")}
          error={errors.action}
        />

        {/* Help text */}
        <p className="text-sm text-gray-600 -mt-2">
          Use format: module.action (e.g., tenant.create, user.update)
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {initialData ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default PermissionForm;
